import { useMemo } from "react";
import { Sparkle, ArrowRight, Clock, Warning } from "@phosphor-icons/react";

// Category → pill styling. Mirrors ServiceRequestInbox's palette so the two
// widgets read as the same domain when a partner glances between them.
const CAT_STYLE = {
  advisory: "bg-indigo-50 text-indigo-800 border-indigo-200",
  compliance: "bg-slate-50 text-slate-800 border-slate-200",
  accounting: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ai: "bg-cyan-50 text-cyan-800 border-cyan-200",
  tax: "bg-blue-50 text-blue-800 border-blue-200",
  custom: "bg-zinc-50 text-zinc-800 border-zinc-200",
};

const CAT_LABEL = {
  advisory: "Advisory",
  compliance: "Compliance",
  accounting: "Accounting",
  ai: "AI",
  tax: "Tax",
  custom: "Other",
};

const dayMs = 24 * 60 * 60 * 1000;

/**
 * Compact top-of-page pulse for pending client service requests.
 * Renders ABOVE the Flight Deck grid so a partner sees the incoming
 * work volume at a glance without hunting for the inbox card below.
 * Clicking the widget smooth-scrolls to the full ServiceRequestInbox
 * card where they can approve / decline individual items.
 */
export function ServiceInboxPulse({ requests = [] }) {
  const {
    total,
    newToday,
    staleCount,
    oldestAgeDays,
    byCategory,
  } = useMemo(() => {
    const now = Date.now();
    const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
    const staleCutoff = now - dayMs; // 24h SLA — anything older is stale
    let oldest = null;
    const cats = {};
    let today = 0;
    let stale = 0;
    for (const r of requests) {
      const ts = new Date(r.created_at).getTime();
      if (ts >= midnight.getTime()) today += 1;
      if (ts < staleCutoff) stale += 1;
      if (oldest === null || ts < oldest) oldest = ts;
      const c = r.service_category || "custom";
      cats[c] = (cats[c] || 0) + 1;
    }
    const oldestAgeDays = oldest === null ? null : Math.max(0, Math.floor((now - oldest) / dayMs));
    return {
      total: requests.length,
      newToday: today,
      staleCount: stale,
      oldestAgeDays,
      byCategory: Object.entries(cats).sort((a, b) => b[1] - a[1]),
    };
  }, [requests]);

  const scrollToInbox = () => {
    const el = document.querySelector('[data-testid="flightdeck-service-inbox"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Accent — emerald when the inbox is empty, rose whenever any request is
  // past its 24h SLA (highest priority — nothing should sit stale), amber
  // when 1-4 are pending but all still fresh, rose again for large backlogs.
  const accent = total === 0
    ? { ring: "ring-emerald-200", num: "text-emerald-700", pip: "bg-emerald-500" }
    : staleCount > 0 || total >= 5
      ? { ring: "ring-rose-200", num: "text-rose-700", pip: "bg-rose-500" }
      : { ring: "ring-amber-200", num: "text-amber-700", pip: "bg-amber-500" };

  return (
    <button
      type="button"
      onClick={total > 0 ? scrollToInbox : undefined}
      data-testid="flightdeck-service-pulse"
      disabled={total === 0}
      className={`group w-full text-left rounded-lg border border-zinc-200 bg-white px-4 py-3 flex flex-wrap items-center gap-4 hover:border-zinc-400 hover:shadow-sm transition-all duration-200 ${total === 0 ? "cursor-default" : "cursor-pointer"}`}
    >
      <div className={`flex items-center gap-3 pr-4 border-r border-zinc-100`}>
        <div className={`w-9 h-9 rounded-md bg-white flex items-center justify-center ring-4 ${accent.ring}`}>
          <Sparkle size={16} weight="fill" className={accent.num} />
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-widest text-zinc-400 leading-none">Pending service requests</p>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span data-testid="flightdeck-service-pulse-count" className={`font-mono text-2xl leading-none ${accent.num}`}>{total}</span>
            {staleCount > 0 && (
              <span
                data-testid="flightdeck-service-pulse-stale"
                title={`${staleCount} pending ${staleCount === 1 ? "request has" : "requests have"} been open more than 24 hours`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-800 bg-rose-50 border border-rose-200 rounded-sm px-1.5 py-0.5"
              >
                <Warning size={10} weight="fill" />
                {staleCount} stale &gt;24h
              </span>
            )}
            {newToday > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                <span className={`w-1.5 h-1.5 rounded-full ${accent.pip} animate-pulse`} />
                {newToday} new today
              </span>
            )}
          </div>
        </div>
      </div>

      {total === 0 && (
        <p className="text-xs text-zinc-500 italic flex-1">Inbox zero — no pending client requests.</p>
      )}

      {total > 0 && (
        <>
          <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0" data-testid="flightdeck-service-pulse-cats">
            {byCategory.map(([cat, n]) => (
              <span
                key={cat}
                className={`text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${CAT_STYLE[cat] || CAT_STYLE.custom}`}
                data-testid={`flightdeck-service-pulse-cat-${cat}`}
              >
                {CAT_LABEL[cat] || cat} · {n}
              </span>
            ))}
          </div>

          {oldestAgeDays !== null && (
            <div
              className={`flex items-center gap-1 text-[11px] shrink-0 ${oldestAgeDays >= 1 ? "text-rose-700 font-semibold" : "text-zinc-500"}`}
              data-testid="flightdeck-service-pulse-oldest"
            >
              <Clock size={11} />
              Oldest {oldestAgeDays === 0 ? "today" : `${oldestAgeDays}d`}
            </div>
          )}

          <span className="text-[11px] text-zinc-500 group-hover:text-zinc-900 flex items-center gap-1 shrink-0 transition-colors">
            Review inbox <ArrowRight size={11} />
          </span>
        </>
      )}
    </button>
  );
}
